import React from 'react';
import autobind from 'autobind-decorator';
import { Grid, Cell } from 'react-ittsu/layout';
import gql from 'fraql';

import BaseServiceModal from '../BaseServiceModal';
import Paragraph from '../Paragraph';
import SectionTitle from '../SectionTitle';
import { ProductCard, Stripe, StripeGroup } from '../../forms';
import { priceItem, applyPrice } from '../../../utils/pricing';
import { specsToItems, itemsToSpecs } from '~/pods/order/utils/services/specs-to-items';
import applyChanges from '~/pods/order/utils/services/specs-to-items/apply-changes';
import { getItemsByService, getFeaturesForProduct, openIntercom, merge } from '~/pods/order/utils';
import { createTranslate, translate as t } from '~/utils/translation';
import { EstateEnums } from '~/pods/order/utils/estate-enums';

import serviceImages from '~/pods/order/images/services';


const tt = createTranslate('pods.order.services.exterior_3d.order');
const tm = createTranslate('pods.order.components.service_modal');


class Exterior3D extends React.Component {

  static fragments = {
    Item: gql`
      fragment _ on Item {
        id
        details {
          ... on Exterior3DDetails {
            droneShooting
            viewCount
          }
        }
        service {
          id @client(type: ProductService)
        }
        product {
          id @client(type: Product)
          custom
          name
          features {
            locale
            content
          }
        }
      }
    `,
  }

  state = {
    custom: null,
    specs: {
      droneShooting: false,
      standard: false,
      viewCount: 0,
    },
    price: undefined,
  };

  componentDidUpdate(prevProps) {
    const { specs } = this.state;
    const { service, estate } = this.props;
    const { service: prevService } = prevProps;
    if (service?.id === 'exterior3d' && ! prevService) {
      const newSpecs = itemsToSpecs({ estate, service });
      const items = getItemsByService(estate).exterior3d || [];
      const customItem = items?.find((item) => item.product.custom);
      const price = items != null ? items.reduce((memo, i) => memo + priceItem(i, this.props.sessionUser.currency.code, true), 0) : 0;
      this.setState({ specs: merge(specs, newSpecs), price, custom: customItem ? true : null });
    }
  }

  render() {
    const { onClickClose, isVisible, service, estate, sessionUser } = this.props;
    const { price, specs: { standard, droneShooting, viewCount }, custom } = this.state;
    const { exterior3d } = serviceImages;
    const items = getItemsByService(estate).exterior3d || [];
    const customItem = items.find((item) => item.product.custom);
    const customFeatures = custom !== null ? getFeaturesForProduct(customItem?.product) : null;
    const isResidential = estate.projectType === EstateEnums.ProjectType.RESIDENTIAL;
    return (
      <BaseServiceModal
        isVisible={isVisible && service?.id === 'exterior3d'}
        title={service?.id ? t(`services.${service.id}`) : ''}
        images={Object.values(exterior3d)}
        price={sessionUser.addCurrency(price)}
        disclaimer={{
          label: tt('disclaimer.text'),
          description: tt('disclaimer.description'),
        }}
        priceDisclaimer={tt('price_disclaimer')}
        onClickConfirm={this._handleClickConfirm}
        confirmAsUpdate={items?.length > 0}
        onClickClose={onClickClose}
        addDisabled={price === 0}>
        <div className="childcontent">
          <Paragraph>
            {tt('description')}
          </Paragraph>
          <SectionTitle>
            {tt('select_number')}
          </SectionTitle>
          <Grid withVGutters withHGutters>
            <Cell size="1of2">
              <ProductCard
                name="standard"
                label={tm('standard')}
                note={tm('from_price', { price: `${applyPrice(isResidential ? { EUR: 850, GBP: 850, USD: 1050 } : { EUR: 950, GBP: 950, USD: 1200 }, sessionUser)}/img` })}
                fullHeight
                formComponent="checkbox"
                features={[{
                  text: tt('features.first'),
                }, {
                  text: tt('features.second'),
                }]}
                value={viewCount}
                onChange={this._handleClickStandard}
                withCounter />
            </Cell>
            <Cell size="1of2">
              <ProductCard
                name="custom"
                label={custom !== null ? `${customItem?.product.name}` : tm('custom')}
                note={custom !== null ? `${sessionUser.addCurrency(customItem?.price)}` : tm('contact_us')}
                formComponent="checkbox"
                fullHeight
                features={customFeatures}
                onChange={this._handleClickCustom}
                value={!! custom} />
            </Cell>
          </Grid>
          <SectionTitle>
            {tt('order_drone_shooting')}
          </SectionTitle>
          <StripeGroup>
            <Stripe
              name="droneShooting"
              title={tt('drone_shooting')}
              subtitle={tm('separate_service')}
              component="switch"
              features={[{
                text: tt('drone_shooting_features.first'),
              }]}
              disabled={! standard}
              value={droneShooting}
              onChange={() => this.setState({ specs: { ...this.state.specs, droneShooting: ! droneShooting } }, this._syncState)} />
          </StripeGroup>
        </div>
      </BaseServiceModal>
    );
  }

  @autobind
  _syncState() {
    const { estate, service } = this.props;
    const { specs } = this.state;
    const changes = specsToItems({ specs, estate, service });
    const updatedEstate = applyChanges(changes, estate);
    const items = getItemsByService(updatedEstate).exterior3d;
    const price = items != null
      ? items
        .map((item) => item.estate != null ? item : { ...item, estate: updatedEstate })
        .reduce((memo, i) => memo + priceItem(i, this.props.sessionUser.currency.code, true), 0)
      : 0;
    this.setState({ price });
    return { changes, updatedEstate, price };
  }

  @autobind
  _handleClickCustom(value) {
    const { custom } = this.state;
    if (custom === null) {
      // open intercom
      openIntercom();
    }
    else {
      this.setState({ custom: ! custom });
    }
  }

  @autobind
  _handleClickStandard(value) {
    const { specs: { droneShooting, viewCount } } = this.state;
    const newValue = value ? value : (viewCount === 0 ? 1 : 0);
    this.setState({
      specs: {
        ...this.state.specs,
        standard: newValue === 0 ? false : true,
        viewCount: newValue,
        droneShooting: newValue === 0 ? false : droneShooting,
      },
    }, this._syncState);
  }

  @autobind
  _handleClickConfirm() {
    const { onModifyService, estate } = this.props;
    const { custom } = this.state;
    const extraRemove = custom === false ? [getItemsByService(estate).exterior3d.find((item) => item.product.custom)] : [];
    const { changes } = this._syncState();
    onModifyService({ ...changes, remove: [ ...changes.remove, ...extraRemove ] });
  }
}


export default Exterior3D;
